import React, { useEffect, useState } from "react";

import { withoutReservedTags } from "@formant/core/common/withoutReservedTags";
import { ICommandTemplate } from "@formant/core/model/ICommandTemplate";
import { ITagSets } from "@formant/core/model/ITagSets";
import { Input, Search } from "@formant/ui-kit";

import { commandTemplateStore } from "../../../../stores";
import { sortCommandTemplatesByDisplayOrder } from "../../../../utils/commandTemplateOrder";
import { ChooseTags } from "../utils/ChooseTags";
import { CommandTemplateToolbar } from "./CommandTemplateToolbar";

interface ICommandTemplateSearchProps {
    onFilteredCommandsChange: (commands: ICommandTemplate[]) => void;
    onCanReorderChange?: (canReorder: boolean) => void;
    listRevision?: number;
}

export const CommandTemplateSearch: React.FC<ICommandTemplateSearchProps> = ({
    onFilteredCommandsChange,
    onCanReorderChange,
    listRevision = 0,
}) => {
    const [originalCommands, setOriginalCommands] = useState<
        ICommandTemplate[]
    >([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedTags, setSelectedTags] = useState<ITagSets>({});
    const [tags, setTags] = useState<ITagSets>({});

    useEffect(() => {
        const getCurrentCommands = async () => {
            let currentCommands = commandTemplateStore.list();
            if (!currentCommands) {
                currentCommands = await commandTemplateStore.fetchList();
            }
            currentCommands = sortCommandTemplatesByDisplayOrder(
                currentCommands
                    ?.filter(val => val.enabled)
                    ?.map(val => ({
                        ...val,
                        tags: Object.fromEntries(
                            Object.entries(withoutReservedTags(val.tags || {})),
                        ),
                    })) || [],
            );

            const currentUsedCommandsTags = currentCommands.reduce(
                (acc, command) => {
                    Object.entries(command.tags || {}).forEach(
                        ([key, value]) => {
                            acc[key] = [value];
                        },
                    );
                    return acc;
                },
                {} as ITagSets,
            );

            setTags(currentUsedCommandsTags);
            setOriginalCommands(currentCommands as ICommandTemplate[]);
            filterCommands(
                currentCommands as ICommandTemplate[],
                searchTerm,
                selectedTags,
            );
        };

        getCurrentCommands().catch(error => {
            console.error(error);
        });
    }, [listRevision]);

    const filterCommands = (
        commands: ICommandTemplate[],
        search: string,
        selectedTags: ITagSets,
    ) => {
        let filtered = commands;

        // Apply search filter
        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(command => {
                const nameMatch = command.name
                    .toLowerCase()
                    .includes(searchLower);
                const tagsMatch = Object.entries(command.tags || {}).some(
                    ([key, value]) =>
                        key.toLowerCase().includes(searchLower) ||
                        value.toLowerCase().includes(searchLower),
                );
                return nameMatch || tagsMatch;
            });
        }

        // Apply tags filter
        if (Object.keys(selectedTags).length > 0) {
            filtered = filtered.filter(command => {
                return Object.entries(selectedTags).every(([key, values]) => {
                    return values.some(value => command.tags?.[key] === value);
                });
            });
        }

        onFilteredCommandsChange(filtered);
        onCanReorderChange?.(!search && Object.keys(selectedTags).length === 0);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(newSearchTerm);
        filterCommands(originalCommands, newSearchTerm, selectedTags);
    };

    const handleTagsChange = (nextTags: ITagSets) => {
        setSelectedTags(nextTags);
        filterCommands(originalCommands, searchTerm, nextTags);
    };

    return (
        <div>
            <CommandTemplateToolbar commandCount={originalCommands.length} />
            <div className="px-4 py-2">
                <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 " />
                    <Input
                        placeholder="Search by name or tags..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>

                <div className="mb-4">
                    <ChooseTags
                        tags={tags}
                        currentTags={{}}
                        onTagsChange={handleTagsChange}
                    />
                </div>
            </div>
        </div>
    );
};
